import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import { V4_DIALOG_CONTENT, V4_ICON_BUTTON, V4_SMALL_SCREEN } from '../../theme/v4Compat';
import Alert from '@mui/material/Alert';
import { notifications } from '../../utils';
import { FarmMedicalSupplySelect } from '../../elements';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { VetsMedicalSupplyViewModel } from '../../models/vets';
import { MedicalSupplyType } from '../../models/injection';

export interface VeterinaryLocationSupplyFeedModalResult {
  farmSupplyId: string;
  dose: number;
}

interface VeterinaryLocationSupplyFeedModalProps {
  opened: boolean;
  isLoaded: boolean;
  farmSupplies: VetsMedicalSupplyViewModel[];
  onModalAction: (result: VeterinaryLocationSupplyFeedModalResult | null) => void;
}

const VeterinaryLocationSupplyFeedModal: React.FC<any> = ({
  opened,
  isLoaded,
  farmSupplies,
  onModalAction
}: VeterinaryLocationSupplyFeedModalProps) => {
  const styles = sxStyles(useTheme());
  const { t } = useTranslation();
  const fullScreen = useMediaQuery(V4_SMALL_SCREEN);

  const [dose, setDose] = React.useState(0);
  const [farmSupplyId, setFarmSupplyId] = React.useState('');

  const closeDialog = (text: string | null) => {
    if (text == null) {
      onModalAction(null);
      return;
    }

    if (!validateForm()) {
      return;
    }

    onModalAction({ farmSupplyId, dose });
    setFarmSupplyId('');
    setDose(0);
  };

  const validateForm = (): boolean => {
    if (!farmSupplyId) {
      notifications.warning(t('f-val-farm-supply'));
      return false;
    }

    return true;
  };

  const handleFarmSupplyChange = (value: string) => {
    setFarmSupplyId(value || '');
  };

  const renderButtonText = () => {
    const item = farmSupplies.find((x) => x.id === farmSupplyId);
    if (item !== null && item?.supplyType === MedicalSupplyType.Vaccination) {
      return t('f-vet-vaccination');
    }

    return t('f-com-feed');
  };

  return (
    <Dialog
      id="feedSupplyDialog"
      open={opened}
      onClose={() => closeDialog(null)}
      sx={styles.dialog}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="form-dialog-title"
    >
      {fullScreen && (
        <AppBar>
          <Toolbar>
            <Box sx={{ width: '100%', justifyContent: 'space-between', display: 'flex' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title={t('f-com-cancel')} arrow>
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={() => closeDialog(null)}
                    aria-label="close"
                    sx={V4_ICON_BUTTON}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
                <Typography variant="h6">{t('f-loc-feed-title')}</Typography>
              </Box>

              <Button
                onClick={() => closeDialog('ok')}
                id="saveLocationBtn"
                startIcon={<Icon>{'bubble_chart'}</Icon>}
                color="inherit"
              >
                {t('f-com-feed')}
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <DialogTitle id="locationFeedSupplyDialogTitle">{t('f-loc-feed-title')}</DialogTitle>

      <DialogContent sx={V4_DIALOG_CONTENT}>
        <Grid container direction="column" spacing={2} sx={styles.updateForm}>
          <Alert sx={styles.mt2} severity="info">
            {t('f-loc-feed-alert')}
          </Alert>

          <Alert sx={styles.mt2} severity="info">
            {t('f-vet-loc-feed-formula')}
          </Alert>

          {isLoaded && (
            <>
              <Grid item xs={12}>
                <FarmMedicalSupplySelect
                  supplies={farmSupplies}
                  value={farmSupplyId}
                  fullWidth
                  onSupplyChange={handleFarmSupplyChange}
                />
              </Grid>

              <TextField
                id="dose-input"
                label={t('f-vet-loc-feed-dose')}
                variant="outlined"
                sx={styles.formControl}
                value={dose}
                size="small"
                type="number"
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  if (newValue >= 0) {
                    setDose(newValue);
                  }
                }}
              />
            </>
          )}
        </Grid>
      </DialogContent>

      {!fullScreen && (
        <DialogActions sx={styles.dialogHeaderFooter}>
          <Button
            onClick={() => closeDialog(null)}
            variant="outlined"
            startIcon={<Icon>{'close'}</Icon>}
            color="primary"
          >
            {t('f-com-cancel')}
          </Button>
          <Button
            onClick={() => closeDialog('ok')}
            id="saveLocationBtn"
            startIcon={<Icon>{'bubble_chart'}</Icon>}
            variant="contained"
            color="primary"
            disabled={farmSupplyId === '' || dose === 0}
          >
            {renderButtonText()}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default VeterinaryLocationSupplyFeedModal;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) => {
  const fieldMaxWidth = 220;
  return {
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column'
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: fieldMaxWidth
    },
    dialogHeaderFooter: {
      background: colors.defaultGrey
    },
    dialog: {
      overflow: 'hidden'
    },
    updateForm: {
      minWidth: 250,
      justifyContent: 'center'
    },
    mt2: {
      marginTop: theme.spacing(1)
    },
    infoCardAvatar: {
      background: theme.palette.primary.main
    }
  } as const;
};
